import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorIcon from '@mui/icons-material/Error';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import moment from 'moment';
import { Link } from 'react-router-dom';
import scssVariables from '../../../../Config.module.scss';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import { DemeterUserModel, DemeterUserStatus, DemeterUserType, ImportDemeterUserResult } from '../../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../../Layouts/NavigationRoutes';
import OverflowCellWithTooltip from '../../../Components/Tables/OverflowCellWithTooltip/OverflowCellWithTooltip';
import { TableDatum, TableHeader } from '../../../Components/Tables/Table/TableDefinitions';
import formattingService from '../../../Services/Formatting/FormattingService';
import languageService from '../../../Services/Language/LanguageService';
import { translationKeys } from '../../../Services/Language/TranslationDefinitions';
import styles from './UsersPage.module.scss';

const expiringDays = 7;

const fullNameCellRenderer = (parameters: TableDatum<DemeterUserModel>) => {
    const { fullName, demeterUserTrialGuid } = parameters;

    return (
        <OverflowCellWithTooltip
            nonTooltipOverrideValue={
                <Link style={{ color: scssVariables.agGridLinkLolor }} to={`${NavigationRoutes.Users}/${demeterUserTrialGuid}/edit`}>
                    {fullName}
                </Link>
            }
            value={fullName}
        />
    );
};

const userStatusCellRenderer = (parameters: TableDatum<DemeterUserModel>) => {
    const style = { backgroundColor: '', color: scssVariables.mainTextColor };
    const { userStatus, userType, trialExpirationDate } = parameters;

    let status = languageService.translate(translationKeys.users.status[userStatus]);
    switch (userStatus) {
        case DemeterUserStatus.Active:
            if (userType !== DemeterUserType.Regular) {
                style.backgroundColor = scssVariables.activeBlockColor;
                break;
            }

            if (moment(trialExpirationDate).diff(moment(), 'days') < expiringDays) {
                style.backgroundColor = scssVariables.expiringBlockColor;
                status = languageService.translate(translationKeys.users.status.Expiring);
                break;
            }

            style.backgroundColor = scssVariables.trialBlockColor;
            style.color = scssVariables.plainWhite;
            status = languageService.translate(translationKeys.users.roles.Regular);
            break;
        case DemeterUserStatus.Deactivated:
            style.backgroundColor = scssVariables.deactivatedBlockColor;
            break;
        case DemeterUserStatus.Expired:
            style.backgroundColor = scssVariables.expiredBlockColor;
            break;
        case DemeterUserStatus.Pending:
        default:
            style.backgroundColor = scssVariables.pendingBlockColor;
            break;
    }

    return (
        <div className={styles.users_status_field} style={style}>
            {status}
        </div>
    );
};

const lastUseDateCellRenderer = (parameters: TableDatum<DemeterUserModel>) => {
    if (!parameters.lastUseDate) {
        return applicationConstants.TablePlaceholderZeroOrEmpty;
    }

    return formattingService.toTimeAgo(parameters.lastUseDate);
};

// Column definitions.
export const usersColumnDefinitions: TableHeader<DemeterUserModel>[] = [
    {
        field: 'fullName',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.name),
        minimumWidth: 150,
        cellRenderer: fullNameCellRenderer,
        pinned: 'left',
    },
    {
        field: 'email',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.email),
        minimumWidth: 150,
    },
    {
        field: 'companyName',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.company),
        minimumWidth: 100,
    },
    {
        field: 'userType',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.role),
        minimumWidth: 100,
        cellRenderer: (parameters) => (
            <OverflowCellWithTooltip
                value={languageService.translate(translationKeys.users.roles[parameters.userType]) || applicationConstants.TablePlaceholderZeroOrEmpty}
            />
        ),
    },
    {
        field: 'markets',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.license),
        minimumWidth: 100,
    },
    {
        field: 'trialExpirationDate',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.expiration),
        minimumWidth: 100,
        dataFormat: 'date',
    },
    {
        field: 'userStatus',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.status),
        minimumWidth: 150,
        cellRenderer: userStatusCellRenderer,
    },
    {
        field: 'lastUseDate',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.lastUseDate),
        minimumWidth: 100,
        cellRenderer: lastUseDateCellRenderer,
    },
    {
        field: 'actions',
        headerValueGetter: () => languageService.translate(translationKeys.words.actions),
        minimumWidth: 140,
    },
];

export const usersImportResultsColumnDefinitions = [
    {
        field: 'name',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.name),
        cellRenderer: (params: TableDatum<DemeterUserModel>) => {
            const element = params;
            return `${element.firstName} ${element.lastName}`;
        },
    },
    {
        field: 'email',
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.email),
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.users.fields.importStatus),
        cellRenderer: (params: TableDatum<ImportDemeterUserResult>) => {
            if (params.isSuccess) {
                return (
                    <div className={styles.import_page_statusCellRenderStyle}>
                        <CheckCircleRoundedIcon color="success" />
                        <p>{languageService.translate(translationKeys.users.import.success)}</p>
                    </div>
                );
            }

            if (params.message === 'User already exists.') {
                return (
                    <div className={styles.import_page_statusCellRenderStyle}>
                        <ReportProblemRoundedIcon color="warning" />
                        <p>{languageService.translate(translationKeys.users.import.skipped)}</p>
                    </div>
                );
            }

            return (
                <div className={styles.import_page_statusCellRenderStyle}>
                    <ErrorIcon color="error" />
                    <p>
                        {languageService.translate(translationKeys.users.import.failed)} - {params.message}
                    </p>
                </div>
            );
        },
    },
];
